import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useNavigate } from '@tanstack/react-router';
import { Command } from 'cmdk';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Building2,
  CheckSquare,
  Clock,
  FileText,
  LayoutDashboard,
  LogOut,
  Monitor,
  Moon,
  Receipt,
  Search,
  Sun,
  Target,
  Users,
} from 'lucide-react';
import { type ReactNode, useCallback, useState } from 'react';

import {
  useDebounced,
  useGlobalSearch,
  type GlobalSearchItem,
} from '@/features/search/use-global-search';
import { useRecentItems } from '@/features/search/recent-items';
import { useSession } from '@/features/auth/use-session';
import { useShortcut } from '@/hooks/use-shortcut';
import { useTheme } from '@/hooks/use-theme';
import { useUiStore } from '@/stores/ui-store';

export function CommandPalette() {
  const open = useUiStore((s) => s.commandOpen);
  const setOpen = useUiStore((s) => s.setCommandOpen);

  // ⌘K toggle (anywhere). `/` open (skipping inputs).
  useShortcut('cmd+k', () => setOpen(!open));
  useShortcut('/', () => setOpen(true), { skipInputs: true });

  return (
    <CommandShell open={open} onOpenChange={setOpen}>
      <CommandBody onClose={() => setOpen(false)} />
    </CommandShell>
  );
}

interface CommandShellProps {
  readonly open: boolean;
  readonly onOpenChange: (next: boolean) => void;
  readonly children: ReactNode;
}

// Dialog + Framer Motion. Pattern forceMount + AnimatePresence porque
// AnimatePresence requiere control manual del montaje, vs el data-state
// que normalmente Radix maneja para animar.
function CommandShell({ open, onOpenChange, children }: CommandShellProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay forceMount asChild>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content forceMount asChild aria-describedby={undefined}>
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
                className="fixed left-1/2 top-[15vh] z-50 w-full max-w-2xl -translate-x-1/2 overflow-hidden rounded-lg border shadow-2xl"
                style={{
                  background: 'var(--color-bg-elevated)',
                  borderColor: 'var(--color-border)',
                }}
              >
                <DialogPrimitive.Title className="sr-only">
                  Paleta de comandos
                </DialogPrimitive.Title>
                {children}
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}

interface CommandBodyProps {
  readonly onClose: () => void;
}

const TYPE_ICON: Record<GlobalSearchItem['type'], ReactNode> = {
  client: <Users className="h-4 w-4" />,
  lead: <Target className="h-4 w-4" />,
  task: <CheckSquare className="h-4 w-4" />,
  invoice: <Receipt className="h-4 w-4" />,
  contract: <Building2 className="h-4 w-4" />,
  quotation: <FileText className="h-4 w-4" />,
};

const TYPE_HEADING: Record<GlobalSearchItem['type'], string> = {
  client: 'Clientes',
  lead: 'Leads',
  task: 'Tareas',
  invoice: 'Facturas',
  contract: 'Contratos OV',
  quotation: 'Cotizaciones',
};

// cmdk hace su propio filtering por defecto contra el `value` interno de
// cada Item. Lo deshabilitamos con `shouldFilter={false}` porque nuestros
// resultados ya vienen filtrados por el backend — si no, cmdk ocultaría
// items que no matchean literalmente con el texto del input.
function CommandBody({ onClose }: CommandBodyProps) {
  const navigate = useNavigate();
  const { setTheme } = useTheme();
  const { signOut, user } = useSession();
  const { items: recent, push: pushRecent } = useRecentItems();

  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 300);
  const search = useGlobalSearch(debounced);
  const data = search.data;
  const isSearching = query.trim().length > 0;

  // Cierra la paleta antes de ejecutar la acción para evitar glitches
  // visuales cuando la acción navega o abre algo nuevo.
  const runAction = useCallback(
    (action: () => void | Promise<void>): (() => void) =>
      () => {
        onClose();
        requestAnimationFrame(() => {
          void action();
        });
      },
    [onClose],
  );

  const handleResultSelect = useCallback(
    (item: GlobalSearchItem) => {
      pushRecent(item);
      onClose();
      requestAnimationFrame(() => {
        void navigate({ to: item.url });
      });
    },
    [navigate, onClose, pushRecent],
  );

  const hasSession = user !== null;
  const isLoading = search.isFetching && debounced.length > 0;

  const groups: ReadonlyArray<{
    readonly type: GlobalSearchItem['type'];
    readonly items: readonly GlobalSearchItem[];
  }> = data
    ? [
        { type: 'client', items: data.clients },
        { type: 'lead', items: data.leads },
        { type: 'task', items: data.tasks },
        { type: 'invoice', items: data.invoices },
        { type: 'contract', items: data.contracts },
        { type: 'quotation', items: data.quotations },
      ]
    : [];

  const totalResults = groups.reduce((acc, g) => acc + g.items.length, 0);

  return (
    <Command label="Paleta de comandos" loop shouldFilter={false}>
      <CommandSearch value={query} onValueChange={setQuery} loading={isLoading} />
      <Command.List className="max-h-[60vh] overflow-y-auto p-2">
        {isSearching ? (
          <>
            {totalResults === 0 && !isLoading && (
              <Command.Empty className="py-6 text-center text-sm text-[color:var(--color-fg-muted)]">
                No se encontraron resultados para "{query}".
              </Command.Empty>
            )}
            {groups
              .filter((g) => g.items.length > 0)
              .map((g) => (
                <CommandSection key={g.type} heading={TYPE_HEADING[g.type]}>
                  {g.items.map((item) => (
                    <CommandResultItem
                      key={`${item.type}-${item.id}`}
                      icon={TYPE_ICON[item.type]}
                      title={item.title}
                      subtitle={item.subtitle}
                      onSelect={() => handleResultSelect(item)}
                    />
                  ))}
                </CommandSection>
              ))}
          </>
        ) : (
          <>
            {recent.length > 0 && (
              <CommandSection heading="Recientes">
                {recent.map((item) => (
                  <CommandResultItem
                    key={`recent-${item.type}-${item.id}`}
                    icon={<Clock className="h-4 w-4" />}
                    title={item.title}
                    subtitle={
                      item.subtitle
                        ? `${TYPE_HEADING[item.type]} · ${item.subtitle}`
                        : TYPE_HEADING[item.type]
                    }
                    onSelect={() => handleResultSelect(item)}
                  />
                ))}
              </CommandSection>
            )}

            <CommandSection heading="Navegación">
              <CommandActionItem
                icon={<LayoutDashboard className="h-4 w-4" />}
                onSelect={runAction(() => {
                  void navigate({ to: '/' });
                })}
              >
                Ir a Dashboard
              </CommandActionItem>
            </CommandSection>

            <CommandSection heading="Apariencia">
              <CommandActionItem
                icon={<Sun className="h-4 w-4" />}
                onSelect={runAction(() => setTheme('light'))}
              >
                Cambiar a tema claro
              </CommandActionItem>
              <CommandActionItem
                icon={<Moon className="h-4 w-4" />}
                onSelect={runAction(() => setTheme('dark'))}
              >
                Cambiar a tema oscuro
              </CommandActionItem>
              <CommandActionItem
                icon={<Monitor className="h-4 w-4" />}
                onSelect={runAction(() => setTheme('system'))}
              >
                Usar tema del sistema
              </CommandActionItem>
            </CommandSection>

            {hasSession && (
              <CommandSection heading="Sesión">
                <CommandActionItem
                  icon={<LogOut className="h-4 w-4" />}
                  onSelect={runAction(async () => {
                    await signOut();
                    void navigate({ to: '/login' });
                  })}
                >
                  Cerrar sesión
                </CommandActionItem>
              </CommandSection>
            )}
          </>
        )}
      </Command.List>
      <CommandFooter />
    </Command>
  );
}

interface CommandSearchProps {
  readonly value: string;
  readonly onValueChange: (next: string) => void;
  readonly loading: boolean;
}

function CommandSearch({ value, onValueChange, loading }: CommandSearchProps) {
  return (
    <div
      className="flex items-center gap-2 border-b px-4 py-3"
      style={{ borderColor: 'var(--color-border)' }}
    >
      <Search className="h-4 w-4 shrink-0" style={{ color: 'var(--color-fg-subtle)' }} />
      <Command.Input
        value={value}
        onValueChange={onValueChange}
        placeholder="Buscar clientes, leads, tareas, facturas, contratos..."
        className="flex-1 bg-transparent text-sm outline-none placeholder:text-[color:var(--color-fg-subtle)]"
      />
      {loading && (
        <span
          className="text-xs"
          style={{ color: 'var(--color-fg-subtle)' }}
          aria-live="polite"
        >
          Buscando…
        </span>
      )}
      <kbd
        className="rounded px-1.5 py-0.5 text-xs"
        style={{ background: 'var(--color-bg-overlay)', color: 'var(--color-fg-subtle)' }}
      >
        esc
      </kbd>
    </div>
  );
}

function CommandFooter() {
  return (
    <div
      className="flex items-center justify-end gap-3 border-t px-4 py-2 text-xs"
      style={{ borderColor: 'var(--color-border)', color: 'var(--color-fg-subtle)' }}
    >
      <span>
        <kbd className="font-sans">↑↓</kbd> navegar
      </span>
      <span>
        <kbd className="font-sans">↵</kbd> ejecutar
      </span>
      <span>
        <kbd className="font-sans">esc</kbd> cerrar
      </span>
    </div>
  );
}

interface CommandSectionProps {
  readonly heading: string;
  readonly children: ReactNode;
}

function CommandSection({ heading, children }: CommandSectionProps) {
  return (
    <Command.Group
      heading={heading}
      className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-[color:var(--color-fg-subtle)]"
    >
      {children}
    </Command.Group>
  );
}

interface CommandActionItemProps {
  readonly icon: ReactNode;
  readonly onSelect: () => void;
  readonly children: ReactNode;
}

function CommandActionItem({ icon, onSelect, children }: CommandActionItemProps) {
  return (
    <Command.Item
      onSelect={onSelect}
      className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm transition-colors data-[selected=true]:bg-[color:var(--color-bg-overlay)] data-[selected=true]:text-[color:var(--color-fg)]"
    >
      <span className="text-[color:var(--color-fg-muted)]">{icon}</span>
      <span>{children}</span>
    </Command.Item>
  );
}

interface CommandResultItemProps {
  readonly icon: ReactNode;
  readonly title: string;
  readonly subtitle: string | null;
  readonly onSelect: () => void;
}

function CommandResultItem({
  icon,
  title,
  subtitle,
  onSelect,
}: CommandResultItemProps) {
  return (
    <Command.Item
      onSelect={onSelect}
      // El value tiene que ser único por item: si dos items comparten value
      // cmdk los colapsa al mismo highlight en flechas. Concatenamos
      // título + subtitulo para que sea distintivo.
      value={`${title} ${subtitle ?? ''}`}
      className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm transition-colors data-[selected=true]:bg-[color:var(--color-bg-overlay)] data-[selected=true]:text-[color:var(--color-fg)]"
    >
      <span className="text-[color:var(--color-fg-muted)]">{icon}</span>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate">{title}</span>
        {subtitle && (
          <span
            className="truncate text-xs"
            style={{ color: 'var(--color-fg-subtle)' }}
          >
            {subtitle}
          </span>
        )}
      </div>
    </Command.Item>
  );
}
